/**
 * Privy API Client
 *
 * API client for Privy verification endpoints.
 * Extends BaseApiClient for proper authentication and error handling.
 *
 * GW-6769: initiateVerification and fetchPrivyStatus now route to
 * gw-signals-api /v1/verifications/identity routes (atomic switch, no fallback).
 */

import { getConfig } from '@/config/runtimeConfig';

import { BaseApiClient } from './baseApiClient';

/**
 * Response from POST /v1/verifications/identity (gw-signals-api)
 */
export interface IdvInitiateResponse {
  clientSecret: string;
  sessionId: string;
}

/**
 * Response from GET /v1/verifications/identity/status (gw-signals-api)
 */
export interface IdvStatusResponse {
  sessionStatus: string;
  hasActiveSession: boolean;
}

/**
 * Client for gw-signals-api /v1/verifications/identity routes.
 * GW-6769: atomic switch — no fallback to gw-functions.
 */
class PrivyVerificationClient extends BaseApiClient {
  constructor() {
    super({
      baseURLFactory: () => getConfig().signalsApiBaseUrl ?? getConfig().apiBaseUrl,
      suppressSessionExpired: true,
    });
  }

  /**
   * Initiate a Stripe Identity Verification session
   * POST /v1/verifications/identity
   *
   * @returns Promise<IdvInitiateResponse> - clientSecret for Stripe and sessionId
   * @throws ApiError on 401 (unauthenticated) or other errors
   */
  async initiateVerification(): Promise<IdvInitiateResponse> {
    return this.post<IdvInitiateResponse>('/v1/verifications/identity');
  }

  /**
   * Fetch the current IDV session status
   * GET /v1/verifications/identity/status
   *
   * @returns Promise<IdvStatusResponse> - sessionStatus for the IDV session
   * @throws ApiError on 401 (unauthenticated) or 404 (no session)
   */
  async fetchPrivyStatus(): Promise<IdvStatusResponse> {
    return this.get<IdvStatusResponse>('/v1/verifications/identity/status');
  }
}

const verificationClient = new PrivyVerificationClient();

/**
 * privyApi aggregates verification client under one export for backward compat.
 */
export const privyApi = {
  initiateVerification: () => verificationClient.initiateVerification(),
  fetchPrivyStatus: () => verificationClient.fetchPrivyStatus(),
};
